/**
 * Page d'Onboarding
 * Wizard multi-étapes pour la configuration initiale de la boutique
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import AIImportWizard from '../components/onboarding/AIImportWizard';
import { logger } from '../utils/logger';

// Types de commerce avec presets
export type ShopType = 'bio' | 'bulk' | 'mixed' | 'custom';

interface ShopPreset {
  id: ShopType;
  name: string;
  description: string;
  icon: string;
  categories: string[];
  defaultTax: number;
  features: string[];
}

const SHOP_PRESETS: ShopPreset[] = [
  {
    id: 'bio',
    name: 'Magasin Bio',
    description: 'Produits biologiques, naturels et éco-responsables',
    icon: '🌿',
    categories: ['Fruits & Légumes Bio', 'Épicerie Bio', 'Produits Laitiers Bio', 'Viandes Bio', 'Cosmétiques Bio'],
    defaultTax: 5.5,
    features: ['Labels bio', 'Traçabilité', 'Producteurs locaux', 'Zéro déchet']
  },
  {
    id: 'bulk',
    name: 'Vrac & Zéro Déchet',
    description: 'Vente en vrac, sans emballage, consigne',
    icon: '♻️',
    categories: ['Céréales & Graines', 'Légumineuses', 'Fruits Secs', 'Épices', 'Huiles & Vinaigres', 'Produits Ménagers'],
    defaultTax: 5.5,
    features: ['Pesée automatique', 'Consigne', 'Contenants réutilisables', 'Vente au poids']
  },
  {
    id: 'mixed',
    name: 'Commerce Mixte',
    description: 'Bio + Vrac + Produits conventionnels',
    icon: '🏪',
    categories: ['Fruits & Légumes', 'Épicerie', 'Vrac', 'Frais', 'Surgelés', 'Hygiène'],
    defaultTax: 20,
    features: ['Multi-format', 'Vrac & Emballé', 'Bio & Conventionnel', 'E-commerce']
  },
  {
    id: 'custom',
    name: 'Configuration Personnalisée',
    description: 'Créer votre propre configuration sur mesure',
    icon: '⚙️',
    categories: [],
    defaultTax: 20,
    features: ['Configuration libre', 'Catégories personnalisées', 'TVA ajustable']
  }
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [shopType, setShopType] = useState<ShopType | null>(null);
  const [shopConfig, setShopConfig] = useState({
    name: '',
    currency: 'EUR',
    defaultTax: 20,
    language: 'fr'
  });

  const totalSteps = 4;
  const selectedPreset = SHOP_PRESETS.find(p => p.id === shopType);

  // Navigation
  const goToNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    // TODO: Sauvegarder la configuration
    logger.log('Configuration finale:', { shopType, shopConfig });
    navigate('/dashboard');
  };

  // Rendu des étapes
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <StepShopType shopType={shopType} onSelect={setShopType} />;
      case 2:
        return (
          <StepConfiguration
            config={shopConfig}
            onChange={setShopConfig}
            preset={selectedPreset}
          />
        );
      case 3:
        return <StepImportProducts preset={selectedPreset} />;
      case 4:
        return <StepSummary shopType={shopType} config={shopConfig} preset={selectedPreset} />;
      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return shopType !== null;
      case 2:
        return shopConfig.name.trim().length > 0;
      case 3:
        return true; // Import optionnel
      case 4:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Bienvenue sur Folies POS
          </h1>
          <p className="text-lg text-gray-600">
            Configurons votre boutique en quelques étapes
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {Array.from({ length: totalSteps }, (_, i) => {
              const stepNum = i + 1;
              const isActive = stepNum === currentStep;
              const isCompleted = stepNum < currentStep;

              return (
                <div key={stepNum} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isActive
                          ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {isCompleted ? '✓' : stepNum}
                    </div>
                    <span className={`text-xs mt-1 ${isActive ? 'font-semibold text-blue-600' : 'text-gray-500'}`}>
                      {stepNum === 1 && 'Type'}
                      {stepNum === 2 && 'Configuration'}
                      {stepNum === 3 && 'Produits'}
                      {stepNum === 4 && 'Validation'}
                    </span>
                  </div>
                  {stepNum < totalSteps && (
                    <div
                      className={`h-1 flex-1 mx-2 rounded transition-all ${
                        isCompleted ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          {renderStep()}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={goToPrevStep}
            disabled={currentStep === 1}
          >
            Précédent
          </Button>

          <div className="text-sm text-gray-500">
            Étape {currentStep} sur {totalSteps}
          </div>

          {currentStep < totalSteps ? (
            <Button
              variant="primary"
              onClick={goToNextStep}
              disabled={!canProceed()}
            >
              Suivant
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleFinish}
            >
              Terminer
            </Button>
          )}
        </div>

        {/* Skip Button */}
        <div className="text-center mt-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Passer l'onboarding
          </button>
        </div>
      </div>
    </div>
  );
}

// === ÉTAPE 1: Sélection du type de boutique ===
function StepShopType({ shopType, onSelect }: { shopType: ShopType | null; onSelect: (type: ShopType) => void }) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Quel type de commerce gérez-vous ?
      </h2>
      <p className="text-gray-600 mb-6">
        Sélectionnez le type qui correspond le mieux à votre activité. Cela nous permettra de préconfigurer votre boutique.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SHOP_PRESETS.map((preset) => (
          <Card
            key={preset.id}
            onClick={() => onSelect(preset.id)}
            className={`cursor-pointer transition-all hover:shadow-lg ${
              shopType === preset.id
                ? 'ring-2 ring-blue-500 shadow-lg'
                : 'hover:ring-2 hover:ring-gray-300'
            }`}
          >
            <div className="text-4xl mb-3">{preset.icon}</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {preset.name}
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              {preset.description}
            </p>
            <div className="flex flex-wrap gap-1">
              {preset.features.slice(0, 3).map((feature) => (
                <Badge key={feature} variant="secondary" size="sm">
                  {feature}
                </Badge>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// === ÉTAPE 2: Configuration de base ===
function StepConfiguration({
  config,
  onChange,
  preset
}: {
  config: any;
  onChange: (config: any) => void;
  preset?: ShopPreset;
}) {
  const handleChange = (field: string, value: any) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Configurons votre boutique
      </h2>
      <p className="text-gray-600 mb-6">
        Renseignez les informations de base de votre commerce.
      </p>

      {preset && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="text-2xl">{preset.icon}</div>
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900">Configuration {preset.name}</h3>
              <p className="text-sm text-blue-700 mt-1">
                TVA par défaut : {preset.defaultTax}% • {preset.categories.length} catégories préchargées
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <Input
          label="Nom de la boutique"
          placeholder="Ex: La Ferme Bio"
          value={config.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
        />

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Devise"
            value={config.currency}
            onChange={(e) => handleChange('currency', e.target.value)}
            options={[
              { value: 'EUR', label: '€ Euro (EUR)' },
              { value: 'USD', label: '$ Dollar (USD)' },
              { value: 'GBP', label: '£ Livre (GBP)' },
              { value: 'CHF', label: 'CHF Franc Suisse' }
            ]}
          />

          <Input
            label="TVA par défaut (%)"
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={config.defaultTax}
            onChange={(e) => handleChange('defaultTax', parseFloat(e.target.value))}
          />
        </div>

        <Select
          label="Langue"
          value={config.language}
          onChange={(e) => handleChange('language', e.target.value)}
          options={[
            { value: 'fr', label: 'Français' },
            { value: 'en', label: 'English' },
            { value: 'es', label: 'Español' },
            { value: 'de', label: 'Deutsch' }
          ]}
        />
      </div>

      {preset && preset.categories.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-2">
            Catégories qui seront créées :
          </h4>
          <div className="flex flex-wrap gap-2">
            {preset.categories.map((cat) => (
              <Badge key={cat} variant="primary">
                {cat}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// === ÉTAPE 3: Import de produits ===
function StepImportProducts({ preset: _preset }: { preset?: ShopPreset }) {
  const [useImport, setUseImport] = useState(false);

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Importez vos produits
      </h2>
      <p className="text-gray-600 mb-6">
        Vous pouvez importer votre catalogue produits maintenant ou le faire plus tard.
      </p>

      {!useImport ? (
        <div className="space-y-4">
          <Card className="border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors">
            <div className="text-center py-8">
              <div className="text-6xl mb-4">📦</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Importer des produits
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Utilisez notre assistant AI pour importer vos produits depuis un fichier CSV, Excel ou PDF
              </p>
              <Button variant="primary" onClick={() => setUseImport(true)}>
                Démarrer l'import
              </Button>
            </div>
          </Card>

          <Card className="bg-gray-50">
            <div className="text-center py-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Créer manuellement
              </h3>
              <p className="text-sm text-gray-600">
                Vous pourrez ajouter vos produits plus tard depuis la page Produits
              </p>
            </div>
          </Card>
        </div>
      ) : (
        <AIImportWizard
          onComplete={() => setUseImport(false)}
          onCancel={() => setUseImport(false)}
        />
      )}
    </div>
  );
}

// === ÉTAPE 4: Récapitulatif ===
function StepSummary({
  shopType: _shopType,
  config,
  preset
}: {
  shopType: ShopType | null;
  config: any;
  preset?: ShopPreset;
}) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Récapitulatif de votre configuration
      </h2>
      <p className="text-gray-600 mb-6">
        Vérifiez les informations avant de finaliser la création de votre boutique.
      </p>

      <div className="space-y-4">
        {/* Type de commerce */}
        {preset && (
          <Card>
            <div className="flex items-start gap-4">
              <div className="text-4xl">{preset.icon}</div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-lg mb-1">
                  {preset.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {preset.description}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {preset.features.map((feature) => (
                    <Badge key={feature} variant="secondary">
                      {feature}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Configuration */}
        <Card>
          <h3 className="font-semibold text-gray-900 mb-3">Configuration</h3>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500">Nom de la boutique</dt>
              <dd className="font-medium text-gray-900">{config.name}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Devise</dt>
              <dd className="font-medium text-gray-900">{config.currency}</dd>
            </div>
            <div>
              <dt className="text-gray-500">TVA par défaut</dt>
              <dd className="font-medium text-gray-900">{config.defaultTax}%</dd>
            </div>
            <div>
              <dt className="text-gray-500">Langue</dt>
              <dd className="font-medium text-gray-900">
                {config.language === 'fr' ? 'Français' : config.language}
              </dd>
            </div>
          </dl>
        </Card>

        {/* Catégories */}
        {preset && preset.categories.length > 0 && (
          <Card>
            <h3 className="font-semibold text-gray-900 mb-3">
              Catégories ({preset.categories.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {preset.categories.map((cat) => (
                <Badge key={cat} variant="primary">
                  {cat}
                </Badge>
              ))}
            </div>
          </Card>
        )}

        {/* Success message */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex gap-3">
            <div className="text-2xl">✅</div>
            <div>
              <h4 className="font-semibold text-green-900">Tout est prêt !</h4>
              <p className="text-sm text-green-700 mt-1">
                Cliquez sur "Terminer" pour créer votre boutique et accéder au tableau de bord.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
